import { useCallback, useState } from 'react';
import { endOfDay, isBefore } from 'date-fns';
import { useLang } from 'hooks/lang';

import {
  actions,
  AddCountryRequestStatus,
  calculateServiceCost,
  FullProject,
  Outsource,
  OutSourceConfig,
  ProjectService,
  TeamServiceWithTeam,
} from 'entities';

import {
  Alert,
  Collapse,
  DatePicker,
  Modal,
  Column,
  Label,
  Button,
  LabeledCheckbox,
} from 'components/Blocks';
import { WrenchIcon } from 'components/Icons';

import { useUser } from 'hooks';

import { Services } from './Services';
import { ServiceCosts } from './Costs';
import { OutsourcingForm } from './Outsource';

import { useProjectProfile } from '../../context';

import { getLocalDateString } from 'utils/date';
import { OrUndefined } from 'types';

interface AddServiceProps {
  children?: never;
  isOpen: boolean;
  close: () => void;
}

const getDefaultOutsource = ({ currency }: FullProject): Outsource => ({
  clientCost: {
    currency,
    value: '',
  },
  providerCost: {
    currency,
    value: '',
  },

  providerName: '',
  provider: '',
  justification: '',
});

export function AddServiceModal({ isOpen, close }: AddServiceProps) {
  const { t } = useLang();
  const loggedUser = useUser();
  const { project, updateProject } = useProjectProfile();

  const [newService, setNewService] =
    useState<Omit<ProjectService, 'id' | 'name' | 'costCalculation'>>();

  const [serviceSelected, setServiceSelected] = useState<TeamServiceWithTeam>();
  const [dueDate, setDueDate] = useState<OrUndefined<Date>>();

  const { id: projectId, countries } = project;

  const closeModal = useCallback(() => {
    setServiceSelected(undefined);
    setDueDate(undefined);

    close();
  }, [close]);

  const handleServiceChange = useCallback(
    (service: TeamServiceWithTeam) => {
      // we add the name, id and costCalculation on our backend
      const projectService = {
        service: service.id,

        costs: Object.fromEntries(
          countries.map((country) => [
            country,
            service.costVariables.map(({ name, type, id }) =>
              type === 'CURRENCY'
                ? { id, name, type, value: '', currency: project.currency }
                : { id, type, name, value: '' },
            ),
          ]),
        ),
      };

      setNewService(projectService);

      setServiceSelected(service);
    },
    [countries, project.currency],
  );

  const serviceTotal =
    newService && serviceSelected
      ? calculateServiceCost({
          ...newService,
          costCalculation: serviceSelected?.costCalculation,
          name: serviceSelected?.name,
          id: serviceSelected?.id,
        })
      : '0.00';

  const actualOutsource = newService?.outsourceConfig
    ? {
        ...newService?.outsourceConfig,
        clientCost: {
          ...newService.outsourceConfig.clientCost,
          value: serviceTotal,
        },
      }
    : null;

  const missingFilling = [
    !newService || !serviceSelected,
    !dueDate,
    !countries.every((country) =>
      serviceSelected?.costVariables.every(({ name, type }) => {
        const costFilled = newService?.costs[country]?.find(
          (c) => c.name === name && c.type === type,
        );

        const valueOk = !!costFilled?.value;

        if (type === 'NUMBER') return valueOk;

        return valueOk && !!costFilled?.currency;
      }),
    ),
    !!actualOutsource &&
      Object.keys(getDefaultOutsource(project)).some((key) => {
        const value = (actualOutsource as OutSourceConfig)[key as keyof Outsource];

        return (
          !value ||
          (typeof value === 'string' && !value?.trim?.()) ||
          (typeof value === 'object' &&
            Object.values(value).some((v) => !v || !(v as string).trim?.()))
        );
      }),
  ].some(Boolean);

  const addService = actions.projects.requestProjectServiceAddition.use({
    block: missingFilling,

    params: {
      ...(newService as ProjectService),
      project: projectId,
      expectedDate: dueDate ? getLocalDateString(dueDate) : '',
      outsourceConfig: actualOutsource || undefined,
    },

    onSuccess: (newTask) => {
      updateProject((old) => ({
        ...old,
        services: [
          ...old.services,
          {
            ...newService,
            id: newTask.serviceConfigId,
            outsourced: !!newService?.outsourceConfig,
          } as ProjectService,
        ],
        tasks: [...old.tasks, newTask],
      }));

      closeModal();
    },
  });

  const shouldDisableDate = (date: Date | null): boolean => {
    if (!date) return false;

    const finishDate = new Date(project.expectedDate as string);

    return isBefore(endOfDay(finishDate), date);
  };

  const handleCheckboxClick = useCallback(() => {
    setNewService((old) => ({
      ...(old as ProjectService),

      outsourceConfig: old?.outsourceConfig ? undefined : getDefaultOutsource(project),
    }));
  }, [project]);

  const setOutsource = useCallback(
    (newData: OutSourceConfig | ((old: OutSourceConfig) => OutSourceConfig)) => {
      setNewService((old) => ({
        ...(old as ProjectService),

        outsourceConfig:
          typeof newData === 'function'
            ? newData(old?.outsourceConfig as OutSourceConfig)
            : newData,
      }));
    },
    [],
  );

  const exceedsExpectedDate = Number(new Date(project.expectedDate as string)) < Number(new Date());

  const addCountryBlockerStatus: Array<
    Exclude<AddCountryRequestStatus, undefined | 'APPROVED' | 'REJECTED'>
  > = ['REQUESTED', 'WAITING_APPROVAL'];

  const canAddService =
    !exceedsExpectedDate &&
    addCountryBlockerStatus.every((status) => project.addCountryRequest?.status !== status);

  const newServiceIsFromPMTeam = serviceSelected?.team.id === loggedUser.team.id;

  return (
    <Modal
      width="min(90vw, 850px)"
      close={closeModal}
      isOpen={isOpen}
      title={t('add-new-service')}
      className="overflow-y-auto"
    >
      <>
        {canAddService ? (
          <Column gap="6">
            <span className="text-sm">
              {t(
                'by-adding-a-service-you-will-automatically-trigger-a-notification-to-the-team-who-will-perform-it',
              )}
            </span>

            <Services service={serviceSelected} setService={handleServiceChange} />

            <ServiceCosts
              serviceSelected={serviceSelected}
              service={newService}
              setService={setNewService as () => void}
            />

            <Column gap="2">
              <Label>{t('due-date')}</Label>

              <DatePicker
                disabled={shouldDisableDate}
                selected={dueDate}
                onSelect={setDueDate}
                disablePast
              />
            </Column>

            <Column>
              <LabeledCheckbox
                checked={!!newService?.outsourceConfig}
                onChange={handleCheckboxClick}
                label={t('outsourced')}
              />

              <Collapse expanded={!!actualOutsource}>
                <OutsourcingForm outsource={actualOutsource} setOutsource={setOutsource} />
              </Collapse>
            </Column>

            {newServiceIsFromPMTeam && (
              <Alert level="info">
                {t(
                  'this-service-is-from-your-team-so-the-task-is-going-to-be-assigned-automatically-to-you',
                )}
              </Alert>
            )}

            <Button disabled={missingFilling} {...addService.buttonProps}>
              <WrenchIcon />
              {t('add-service')}
            </Button>
          </Column>
        ) : (
          <Alert level="info">
            {t(
              exceedsExpectedDate
                ? 'there-are-no-dates-available-to-add-a-new-service-please-adjust-project-date-before-proceeding'
                : 'there-is-a-country-add-request-in-progress-please-wait-for-the-response',
            )}
          </Alert>
        )}
      </>
    </Modal>
  );
}
